import {
  Button,
  ButtonSize,
  ButtonVariant,
  Show,
  TextSkeleton,
  ButtonSkeleton,
  BadgeSkeleton,
  BadgeSize
} from "shared/components";
import Drawer from "react-modern-drawer";
import { Cancel01Icon, CircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import { HugeiconsIcon } from "@hugeicons/react";

type TaskDrawerSkeletonProps = {
  open: boolean;
  onClose?: () => void;
  isMobile?: boolean;
};

export const TaskDrawerSkeleton = ({
  open,
  onClose,
  isMobile = false
}: TaskDrawerSkeletonProps) => {
  return (
    <Drawer
      className="bg-background-1000 border-alpha-100 rounded-lg border"
      direction={isMobile ? "bottom" : "right"}
      size={isMobile ? 380 : 520}
      open={open}
      onClose={onClose}
      lockBackgroundScroll
    >
      <Show if={open}>
        <div className="flex h-screen flex-col">
          <div className="sticky top-0 z-50 border-b border-alpha-80 px-6 py-4 flex justify-between">
            <div className="space-y-2 max-w-[448px] flex-1">
              <p className="text-content-400 font-medium text-base">Task</p>
              <div className="flex items-start gap-2">
                <div className="h-[24px] w-[24px] flex justify-center items-center">
                  <HugeiconsIcon
                    icon={CircleIcon}
                    className="text-content-300"
                    size={20}
                  />
                </div>
                <BadgeSkeleton size={BadgeSize.LG} className="w-full" />
              </div>
            </div>
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              borderLess
              HugeIcon={Cancel01Icon}
              onClick={onClose}
              aria-label="Close drawer"
              className={cx(
                "absolute right-6 top-8 bottom-8 !p-0 !w-[24px] !gap-0 !h-[24px] !min-h-[24px]",
                "!text-content-1000",
                "!hover:bg-transparent !focus:bg-transparent !active:bg-transparent !shadow-none"
              )}
            />
          </div>
          <div className="border-b border-alpha-40 px-6 pt-6 h-[60px] sticky top-[72px] z-40 bg-background-1000">
            <div className="flex items-center gap-6 h-[36px]">
              <TextSkeleton size="sm" className="w-[50px]" />
              <TextSkeleton size="sm" className="w-[50px]" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-4">
              <div className="flex flex-row items-start gap-2">
                <TextSkeleton size="sm" className="w-[100px]" />
                <BadgeSkeleton size={BadgeSize.BASE} className="w-full" />
              </div>
              <div className="flex flex-row items-start gap-2">
                <TextSkeleton size="sm" className="w-[100px]" />
                <BadgeSkeleton size={BadgeSize.BASE} className="w-full" />
              </div>
              <div className="flex flex-row items-start gap-2">
                <TextSkeleton size="sm" className="w-[100px]" />
                <BadgeSkeleton size={BadgeSize.BASE} className="w-full" />
              </div>
            </div>
            <div className="my-4 h-px w-full bg-alpha-40" />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <TextSkeleton size="sm" className="w-[120px]" />
                <TextSkeleton size="lg" className="w-full" />
              </div>
              <div className="flex items-center gap-2">
                <TextSkeleton size="sm" className="w-[120px]" />
                <TextSkeleton size="lg" className="w-full" />
              </div>
              <div className="flex items-center gap-2">
                <TextSkeleton size="sm" className="w-[120px]" />
                <TextSkeleton size="lg" className="w-full" />
              </div>
              <div className="flex items-center gap-2">
                <TextSkeleton size="sm" className="w-[120px]" />
                <TextSkeleton size="xl" className="w-full" />
              </div>
            </div>
            <div className="my-4 h-px w-full bg-alpha-40" />
            <div className="space-y-2">
              <TextSkeleton size="sm" className="w-[120px]" />
              <BadgeSkeleton size={BadgeSize.LG} className="h-[100px] w-full" />
            </div>
          </div>
          <div className="border-t border-alpha-100 px-6 py-4">
            <div className="flex justify-end gap-2">
              <ButtonSkeleton size={ButtonSize.BASE} className="w-[125px]" />
              <ButtonSkeleton size={ButtonSize.BASE} className="w-[125px]" />
            </div>
          </div>
        </div>
      </Show>
    </Drawer>
  );
};
